/**
 * Moteur de calcul pour le module Budget Prévisionnel 5 ans
 *
 * Ce module calcule les montants annuels pour chaque ligne de prévisionnel
 * en fonction des règles définies (fixed, growth, per_unit, threshold)
 */

export interface ForecastDimension {
  id: string;
  code: string;
  label: string;
  unit_name: string;
}

export interface ForecastDimensionValue {
  dimension_id: string;
  year_index: number;
  value: number;
}

export interface ForecastLineItem {
  id: string;
  type: 'expense' | 'revenue';
  section: string;
  label: string;
  rule_type: 'fixed' | 'growth' | 'per_unit' | 'threshold';
  base_amount: number;
  growth_rate: number | null;
  dimension_id: string | null;
  amount_per_unit: number | null;
  threshold_unit_value: number | null;
  threshold_amount: number | null;
  active_from_year: number;
  active_to_year: number;
  sort_order: number;
}

export interface ForecastOverride {
  line_item_id: string;
  year_index: number;
  override_amount: number;
  reason: string | null;
}

export interface YearlyAmount {
  line_item_id: string;
  year_index: number;
  amount: number;
  is_override: boolean;
}

export interface ForecastCalculation {
  yearly_amounts: YearlyAmount[];
  totals_by_year: {
    year_index: number;
    total_expenses: number;
    total_revenues: number;
    net_result: number;
  }[];
  totals_by_section: {
    type: 'expense' | 'revenue';
    section: string;
    amounts_by_year: { year_index: number; amount: number }[];
  }[];
}

/**
 * Calcule le montant d'une ligne pour une année donnée
 */
function calculateLineAmount(
  lineItem: ForecastLineItem,
  yearIndex: number,
  dimensionValues: ForecastDimensionValue[],
  overrides: ForecastOverride[]
): number {
  // Vérifier si la ligne est active pour cette année
  if (yearIndex < lineItem.active_from_year || yearIndex > lineItem.active_to_year) {
    return 0;
  }

  // Vérifier s'il y a un override pour cette année
  const override = overrides.find(
    o => o.line_item_id === lineItem.id && o.year_index === yearIndex
  );
  if (override) {
    return override.override_amount;
  }

  // Calcul selon le rule_type
  switch (lineItem.rule_type) {
    case 'fixed':
      return lineItem.base_amount;

    case 'growth': {
      // Croissance composée année après année
      const rate = (lineItem.growth_rate || 0) / 100;
      const yearsSinceStart = yearIndex - 1; // Année 1 = index 0 pour la croissance
      return lineItem.base_amount * Math.pow(1 + rate, yearsSinceStart);
    }

    case 'per_unit': {
      if (!lineItem.dimension_id || lineItem.amount_per_unit === null) {
        return 0;
      }

      // Trouver la valeur de la dimension pour cette année
      const dimValue = dimensionValues.find(
        dv => dv.dimension_id === lineItem.dimension_id && dv.year_index === yearIndex
      );

      if (!dimValue) {
        return 0;
      }

      return lineItem.amount_per_unit * dimValue.value;
    }

    case 'threshold': {
      if (
        !lineItem.dimension_id ||
        lineItem.threshold_unit_value === null ||
        lineItem.threshold_amount === null
      ) {
        return 0;
      }

      // Trouver la valeur de la dimension pour cette année
      const dimValue = dimensionValues.find(
        dv => dv.dimension_id === lineItem.dimension_id && dv.year_index === yearIndex
      );

      if (!dimValue) {
        return 0;
      }

      // Si la valeur atteint ou dépasse le seuil, on retourne le montant threshold
      return dimValue.value >= lineItem.threshold_unit_value ? lineItem.threshold_amount : 0;
    }

    default:
      return 0;
  }
}

/**
 * Calcule tous les montants pour un scénario
 */
export function calculateForecast(
  lineItems: ForecastLineItem[],
  dimensionValues: ForecastDimensionValue[],
  overrides: ForecastOverride[],
  yearsCount: number = 5
): ForecastCalculation {
  const yearlyAmounts: YearlyAmount[] = [];

  // Calculer le montant pour chaque ligne et chaque année
  lineItems.forEach(lineItem => {
    for (let yearIndex = 1; yearIndex <= yearsCount; yearIndex++) {
      const hasOverride = overrides.some(
        o => o.line_item_id === lineItem.id && o.year_index === yearIndex
      );

      const amount = calculateLineAmount(lineItem, yearIndex, dimensionValues, overrides);

      yearlyAmounts.push({
        line_item_id: lineItem.id,
        year_index: yearIndex,
        amount,
        is_override: hasOverride
      });
    }
  });

  // Calculer les totaux par année
  const totalsByYear = [];
  for (let yearIndex = 1; yearIndex <= yearsCount; yearIndex++) {
    let totalExpenses = 0;
    let totalRevenues = 0;

    lineItems.forEach(lineItem => {
      const yearAmount = yearlyAmounts.find(
        ya => ya.line_item_id === lineItem.id && ya.year_index === yearIndex
      );

      if (yearAmount) {
        if (lineItem.type === 'expense') {
          totalExpenses += yearAmount.amount;
        } else {
          totalRevenues += yearAmount.amount;
        }
      }
    });

    totalsByYear.push({
      year_index: yearIndex,
      total_expenses: totalExpenses,
      total_revenues: totalRevenues,
      net_result: totalRevenues - totalExpenses
    });
  }

  // Calculer les totaux par section
  const sectionMap = new Map<string, { type: 'expense' | 'revenue'; section: string }>();
  lineItems.forEach(li => {
    const key = `${li.type}:${li.section}`;
    if (!sectionMap.has(key)) {
      sectionMap.set(key, { type: li.type, section: li.section });
    }
  });

  const totalsBySection = Array.from(sectionMap.values()).map(({ type, section }) => {
    const amountsByYear = [];

    for (let yearIndex = 1; yearIndex <= yearsCount; yearIndex++) {
      let sectionTotal = 0;

      lineItems
        .filter(li => li.type === type && li.section === section)
        .forEach(lineItem => {
          const yearAmount = yearlyAmounts.find(
            ya => ya.line_item_id === lineItem.id && ya.year_index === yearIndex
          );
          if (yearAmount) {
            sectionTotal += yearAmount.amount;
          }
        });

      amountsByYear.push({
        year_index: yearIndex,
        amount: sectionTotal
      });
    }

    return {
      type,
      section,
      amounts_by_year: amountsByYear
    };
  });

  return {
    yearly_amounts: yearlyAmounts,
    totals_by_year: totalsByYear,
    totals_by_section: totalsBySection
  };
}

/**
 * Formate une période d'exercice (ex: "Sept 2024 - Août 2025")
 */
export function formatFiscalYear(startDate: Date, yearIndex: number): string {
  const startYear = startDate.getFullYear() + (yearIndex - 1);
  const startMonth = startDate.getMonth();

  const yearStartDate = new Date(startYear, startMonth, 1);
  const yearEndDate = new Date(startYear + 1, startMonth, 0); // Dernier jour du mois précédent

  const monthNames = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
    'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
  ];

  const startMonthName = monthNames[yearStartDate.getMonth()];
  const endMonthName = monthNames[yearEndDate.getMonth()];

  return `${startMonthName} ${yearStartDate.getFullYear()} - ${endMonthName} ${yearEndDate.getFullYear()}`;
}

/**
 * Récupère les sections suggérées pour les charges
 */
export function getSuggestedExpenseSections(): string[] {
  return [
    'Personnel',
    'Frais généraux',
    'Investissements',
    'Charges financières',
    'Autres charges'
  ];
}

/**
 * Récupère les sections suggérées pour les produits
 */
export function getSuggestedRevenueSections(): string[] {
  return [
    'Autofinancement',
    'Subventions publiques',
    'Fondations et mécénat',
    'Amorçage',
    'Autres produits'
  ];
}
